import type { TParameter, TExperiment } from './experiments';

import { ELocalStorageKeys } from '@/features/store/ELocalStorageKeys';
import { isClient, useLocalStorage } from '@vueuse/core';
import { defaultConfig } from './experiments';
import config from './config.json';

import { initializeApp } from 'firebase/app';
import {
  getRemoteConfig,
  fetchAndActivate,
  type RemoteConfig,
  getAll,
} from 'firebase/remote-config';

export type TConfig = { [key: string]: TParameter };
const PROD_HOST = 'mm.ru';

class FirebaseConfigFetcherClass {
  private readonly CONFIG_FETCH_TIMEOUT_MS = 3000;
  private readonly localStorageRef = useLocalStorage<any>(ELocalStorageKeys.MM__EXPERIMENTS, {});

  private remoteConfig!: RemoteConfig;
  private actualConfig?: TConfig;
  private configFetchingPromise?: Promise<TConfig>;

  public async getConfig(): Promise<TConfig> {
    if (this.actualConfig) return this.actualConfig;

    // PROD_HOST мешает тесту
    const isProdHost = isClient && window.location.host === PROD_HOST;

    if (!isProdHost) {
      this.actualConfig = this.getDefaultConfigValues();
      return this.actualConfig;
    }

    if (this.configFetchingPromise) return this.configFetchingPromise;

    this.configFetchingPromise = new Promise((resolve) => {
      const fallbackTimeout = setTimeout(() => {
        this.actualConfig = this.getDefaultConfigValues();
        resolve(this.actualConfig);
      }, this.CONFIG_FETCH_TIMEOUT_MS);

      try {
        fetchAndActivate(this.remoteConfig).then(() => {
          clearTimeout(fallbackTimeout);
          this.actualConfig = this.getAllConfigValues(getAll(this.remoteConfig));
          resolve(this.actualConfig);
        });
      } catch (e) {
        clearTimeout(fallbackTimeout);
        this.actualConfig = this.getDefaultConfigValues();
        resolve(this.actualConfig);
      }
    });

    return this.configFetchingPromise;
  }

  public initConfig(): void {
    this.remoteConfig = getRemoteConfig(initializeApp(config));
    this.remoteConfig.defaultConfig = defaultConfig;

    this.getConfig().then((config) => {
      this.localStorageRef.value = JSON.stringify(this.getExperimentsHeader(config));
    });
  }

  private getExperimentsHeader(config: TConfig): string {
    return (
      Object.entries(config)
        .filter(
          ([key, value]) => typeof value === 'object' && value['experiment_id'] && value['group_id']
        )
        .map(
          ([_, value]) =>
            `${(value as TExperiment).experiment_id}:${(value as TExperiment).group_id}`
        )
        .join(',') || ''
    );
  }

  private getDefaultConfigValues(): TConfig {
    return Object.fromEntries(
      Object.entries(defaultConfig).map(([key, value]) => [key, this.getConfigValue(value)])
    );
  }

  private getAllConfigValues(configValues: { [key: string]: any }): TConfig {
    return Object.fromEntries(
      // @ts-ignore
      Object.entries(configValues).map(([key, value]) => [
        key,
        this.getConfigValue(value['_value']),
      ])
    );
  }

  private getConfigValue(value: string): any {
    let result = '';

    try {
      result = JSON.parse(value);
    } catch (e) {
      console.error(e);
    }

    return result;
  }
}

export const FirebaseConfigFetcher = new FirebaseConfigFetcherClass();
